import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowRight, Target, TrendingUp, Zap } from 'lucide-react';
import { yearlyTotals, objectifs, tractionActuelle, preSeedInvestment, objectifsY1 } from '../mock';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';
import { useIsMobile } from '../hooks/useIsMobile';
import NavigationControls from '../components/NavigationControls';
import AnimatedCounter from '../components/AnimatedCounter';
import ProgressBar from '../components/ProgressBar';
import VideoPlayer from '../components/VideoPlayer';

export default function Home() {
  const { language } = useLanguage();
  const isMobile = useIsMobile();
  const t = translations[language];

  // Legacy 5-year metrics no longer used directly; new home uses Y1 + traction
  void yearlyTotals;
  void objectifs;
  void isMobile;

  return (
    <div className="min-h-screen p-4 sm:p-6 lg:p-8">
      {/* Badge Partie */}
      <div className="mb-4 flex items-center gap-2 text-sm max-w-7xl mx-auto">
        <span className="w-3 h-3 bg-purple-500 rounded-sm"></span>
        <span className="text-purple-400 font-semibold">{language === 'fr' ? 'PARTIE I – STRATÉGIE DE MARCHÉ' : 'PART I – MARKET STRATEGY'}</span>
        <span className="text-gray-500 ml-2">{language === 'fr' ? 'Page 1 de 4' : 'Page 1 of 4'}</span>
      </div>

      {/* Hero Section */}
      <div className="max-w-7xl mx-auto mb-8 sm:mb-12">
        <div className="text-center mb-6 sm:mb-8">
          <video 
            autoPlay 
            loop 
            muted 
            playsInline
            poster={process.env.REACT_APP_LOGO_IMAGE_URL}
            className="h-24 sm:h-28 lg:h-32 w-auto mx-auto mb-4 sm:mb-6 drop-shadow-[0_0_30px_rgba(168,213,48,0.6)] hover:drop-shadow-[0_0_50px_rgba(168,213,48,0.9)] transition-all duration-300"
          >
            <source src={process.env.REACT_APP_LOGO_VIDEO_URL} type="video/mp4" />
            <img 
              src={process.env.REACT_APP_LOGO_IMAGE_URL} 
              alt="EcoLunch" 
              className="h-24 sm:h-28 lg:h-32 w-auto"
            />
          </video>
          <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold mb-3 sm:mb-4">
            <span className="text-white">{t.home.title}</span>
            <br />
            <span className="text-lime-400 drop-shadow-[0_0_20px_rgba(168,213,48,0.8)]">EcoLunch</span>
          </h1>
          <p className="text-base sm:text-lg lg:text-xl text-gray-300 max-w-3xl mx-auto px-4">
            {t.home.subtitle}
          </p>
        </div>

        {/* MENTION VERTICAL REPAS - Initiateur de la plateforme */}
        <div className="mb-6 p-5 bg-gradient-to-r from-purple-500/15 via-cyan-500/10 to-transparent rounded-2xl border-2 border-purple-500/40" data-testid="vertical-repas-banner">
          <div className="flex items-start gap-4">
            <div className="px-3 py-1 bg-purple-500 text-white text-[10px] font-bold rounded-full uppercase tracking-wider whitespace-nowrap">
              {language === 'fr' ? 'Vertical #1' : 'Vertical #1'}
            </div>
            <div className="flex-1">
              <h3 className="text-lg sm:text-xl font-bold text-purple-300 mb-1">
                {language === 'fr' ? 'Repas scolaires & garderies — Initiateur de la plateforme' : 'School & Daycare Meals — Platform Initiator'}
              </h3>
              <p className="text-sm text-gray-300 leading-relaxed">
                {language === 'fr'
                  ? 'Ce plan d\'affaires couvre exclusivement le vertical des repas (écoles + garderies). Il s\'agit du point d\'entrée stratégique d\'EcoTech Système — la première brique d\'une infrastructure SaaS + FinTech / Merchant of Record qui s\'étendra progressivement vers d\'autres verticales institutionnelles (camps, sports parascolaires, frais scolaires, services de garde étendus, etc.).'
                  : 'This business plan covers exclusively the meals vertical (schools + daycares). It is EcoTech Système\'s strategic entry point — the first building block of a SaaS + FinTech / Merchant of Record infrastructure that will progressively extend to other institutional verticals (camps, after-school sports, school fees, extended childcare services, etc.).'}
              </p>
            </div>
          </div>
        </div>

        {/* TRACTION ACTUELLE - Banner haut de page pour banquiers */}
        <Card className="bg-gradient-to-br from-lime-500/20 via-cyan-500/15 to-orange-500/10 border-2 border-lime-500/50 backdrop-blur-sm mb-8 shadow-[0_0_30px_rgba(168,213,48,0.2)]" data-testid="traction-banner">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-3">
              <span className="px-3 py-1 bg-lime-500 text-black text-xs font-bold rounded-full uppercase tracking-wider">
                {language === 'fr' ? 'Traction validée' : 'Validated Traction'}
              </span>
              <span className="text-gray-300 text-sm">
                {language === 'fr' ? 'en moins d\'un mois' : 'in less than one month'}
              </span>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 sm:gap-8">
              <div>
                <p className="text-3xl sm:text-5xl font-bold text-lime-400 leading-none">{tractionActuelle.traiteurs}</p>
                <p className="text-xs sm:text-sm text-gray-300 mt-2 uppercase tracking-wider">{language === 'fr' ? 'Traiteurs signés' : 'Caterers signed'}</p>
              </div>
              <div>
                <p className="text-3xl sm:text-5xl font-bold text-cyan-400 leading-none">{tractionActuelle.ecoles}</p>
                <p className="text-xs sm:text-sm text-gray-300 mt-2 uppercase tracking-wider">{language === 'fr' ? 'Écoles' : 'Schools'}</p>
              </div>
              <div>
                <p className="text-3xl sm:text-5xl font-bold text-orange-400 leading-none">{tractionActuelle.garderies}</p>
                <p className="text-xs sm:text-sm text-gray-300 mt-2 uppercase tracking-wider">{language === 'fr' ? 'Garderies' : 'Daycares'}</p>
              </div>
            </div>
            <p className="text-xs sm:text-sm text-gray-400 mt-4 border-t border-lime-500/20 pt-3">
              {language === 'fr'
                ? `Soit ${tractionActuelle.etablissements} établissements engagés — la machine commerciale est prouvée.`
                : `That is ${tractionActuelle.etablissements} engaged institutions — the sales machine is proven.`}
            </p>
          </CardContent>
        </Card>

        {/* Key Metrics Cards with animated counters */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-8 sm:mb-12">
          <Card className="bg-black/60 border-lime-500/30 backdrop-blur-sm hover:border-lime-500/60 hover:shadow-2xl hover:shadow-lime-500/30 hover:scale-105 transition-all duration-300">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-lime-500/20 rounded-lg">
                  <Target className="h-5 w-5 sm:h-6 sm:w-6 text-lime-400" />
                </div>
                <CardTitle className="text-white text-base sm:text-lg">{language === 'fr' ? 'Pré-seed' : 'Pre-seed'}</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-2xl sm:text-3xl font-bold text-lime-400 mb-2">
                <AnimatedCounter value={500} duration={2000} suffix="K $" />
              </p>
              <CardDescription className="text-gray-400 text-sm">
                {language === 'fr' ? '6 mois (Sept-Fév) • ~10% equity' : '6 months (Sep-Feb) • ~10% equity'}
              </CardDescription>
              <ProgressBar value={preSeedInvestment.useOfFunds.gtm} max={preSeedInvestment.total} label={language === 'fr' ? 'GTM direct 350K $' : 'Direct GTM $350K'} color="lime" className="mt-4" />
            </CardContent>
          </Card>

          <Card className="bg-black/60 border-cyan-500/30 backdrop-blur-sm hover:border-cyan-500/60 hover:shadow-2xl hover:shadow-cyan-500/30 hover:scale-105 transition-all duration-300">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-cyan-500/20 rounded-lg">
                  <TrendingUp className="h-5 w-5 sm:h-6 sm:w-6 text-cyan-400" />
                </div>
                <CardTitle className="text-white text-base sm:text-lg">{language === 'fr' ? 'Revenu annualisé M12' : 'Annualized revenue M12'}</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-2xl sm:text-3xl font-bold text-cyan-400 mb-2">
                <AnimatedCounter value={objectifsY1.realiste.revenuAnnualiseM12 / 1000000} duration={2500} decimals={2} suffix="M $" />
              </p>
              <CardDescription className="text-gray-400 text-sm">
                {language === 'fr' ? `Scénario réaliste — Min: ${(objectifsY1.minimum.revenuAnnualiseM12/1000000).toFixed(2)}M $` : `Realistic scenario — Min: $${(objectifsY1.minimum.revenuAnnualiseM12/1000000).toFixed(2)}M`}
              </CardDescription>
              <ProgressBar value={objectifsY1.realiste.revenuAnnualiseM12} max={objectifsY1.realiste.revenuAnnualiseM12} label={language === 'fr' ? 'Objectif Année 1' : 'Year 1 target'} color="cyan" className="mt-4" />
            </CardContent>
          </Card>

          <Card className="bg-black/60 border-orange-500/30 backdrop-blur-sm hover:border-orange-500/60 hover:shadow-2xl hover:shadow-orange-500/30 hover:scale-105 transition-all duration-300 sm:col-span-2 lg:col-span-1">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-500/20 rounded-lg">
                  <Zap className="h-5 w-5 sm:h-6 sm:w-6 text-orange-400" />
                </div>
                <CardTitle className="text-white text-base sm:text-lg">{language === 'fr' ? 'GMV annualisé M12' : 'Annualized GMV M12'}</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-2xl sm:text-3xl font-bold text-orange-400 mb-2">
                <AnimatedCounter value={objectifsY1.realiste.m12.gmvAnnualise / 1000000} duration={2500} decimals={0} suffix="M $" />
              </p>
              <CardDescription className="text-gray-400 text-sm">
                {language === 'fr' ? `${objectifsY1.realiste.m12.repasJour.toLocaleString('fr-CA')} repas/jour • 3 000 établissements` : `${objectifsY1.realiste.m12.repasJour.toLocaleString('en-CA')} meals/day • 3,000 institutions`}
              </CardDescription>
              <ProgressBar value={objectifsY1.realiste.m12.gmvAnnualise} max={objectifsY1.realiste.m12.gmvAnnualise * 1.15} label={language === 'fr' ? 'Volume traité' : 'Volume processed'} color="orange" className="mt-4" />
            </CardContent>
          </Card>
        </div>

        {/* Value Proposition */}
        <Card className="bg-gradient-to-br from-lime-500/10 via-black/60 to-cyan-500/10 border-lime-500/30 backdrop-blur-sm mb-8 sm:mb-12">
          <CardHeader>
            <CardTitle className="text-xl sm:text-2xl text-white">{t.home.valueProposition}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-gray-300 leading-relaxed text-sm sm:text-base">
              {t.home.valuePropDesc}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mt-4 sm:mt-6">
              <div className="p-3 sm:p-4 bg-black/40 rounded-lg border border-lime-500/20">
                <h3 className="text-lime-400 font-semibold mb-2 text-sm sm:text-base">{t.home.saasModel}</h3>
                <p className="text-xs sm:text-sm text-gray-400">{t.home.saasDesc}</p>
              </div>
              <div className="p-3 sm:p-4 bg-black/40 rounded-lg border border-cyan-500/20">
                <h3 className="text-cyan-400 font-semibold mb-2 text-sm sm:text-base">{t.home.fintech}</h3>
                <p className="text-xs sm:text-sm text-gray-400">{t.home.fintechDesc}</p>
              </div>
              <div className="p-3 sm:p-4 bg-black/40 rounded-lg border border-orange-500/20">
                <h3 className="text-orange-400 font-semibold mb-2 text-sm sm:text-base">{t.home.quebec}</h3>
                <p className="text-xs sm:text-sm text-gray-400">{t.home.quebecDesc}</p>
              </div>
              <div className="p-3 sm:p-4 bg-black/40 rounded-lg border border-lime-500/20">
                <h3 className="text-lime-400 font-semibold mb-2 text-sm sm:text-base">{t.home.profitable}</h3>
                <p className="text-xs sm:text-sm text-gray-400">{t.home.profitableDesc}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Vidéo Présentation EcoLunch */}
        <VideoPlayer
          videoUrl="https://customer-assets.emergentagent.com/job_8a79716d-d35e-4a82-acf8-054650228f44/artifacts/6x6axo0x_Pr%C3%A9sentation%20Ecolunch.mp4"
          posterUrl={process.env.REACT_APP_LOGO_IMAGE_URL}
          title={language === 'fr' ? 'Présentation EcoLunch' : 'EcoLunch Presentation'}
        />

        {/* Quick Links */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          <Link to="/resume-executif">
            <Card className="bg-black/60 border-lime-500/30 hover:border-lime-500/60 transition-all duration-300 hover:shadow-lg hover:shadow-lime-500/20 h-full cursor-pointer group">
              <CardHeader>
                <CardTitle className="text-white group-hover:text-lime-400 transition-colors flex items-center justify-between text-base sm:text-lg">
                  {t.home.resumeTitle}
                  <ArrowRight className="h-4 w-4 sm:h-5 sm:w-5 opacity-0 group-hover:opacity-100 transition-opacity" />
                </CardTitle>
                <CardDescription className="text-gray-400 text-sm">
                  {t.home.resumeDesc}
                </CardDescription>
              </CardHeader>
            </Card>
          </Link>

          <Link to="/previsions">
            <Card className="bg-black/60 border-cyan-500/30 hover:border-cyan-500/60 transition-all duration-300 hover:shadow-lg hover:shadow-cyan-500/20 h-full cursor-pointer group">
              <CardHeader>
                <CardTitle className="text-white group-hover:text-cyan-400 transition-colors flex items-center justify-between text-base sm:text-lg">
                  {t.home.forecastTitle}
                  <ArrowRight className="h-4 w-4 sm:h-5 sm:w-5 opacity-0 group-hover:opacity-100 transition-opacity" />
                </CardTitle>
                <CardDescription className="text-gray-400 text-sm">
                  {t.home.forecastDesc}
                </CardDescription>
              </CardHeader>
            </Card>
          </Link>

          <Link to="/roadmap">
            <Card className="bg-black/60 border-orange-500/30 hover:border-orange-500/60 transition-all duration-300 hover:shadow-lg hover:shadow-orange-500/20 h-full cursor-pointer group sm:col-span-2 lg:col-span-1">
              <CardHeader>
                <CardTitle className="text-white group-hover:text-orange-400 transition-colors flex items-center justify-between text-base sm:text-lg">
                  {t.home.roadmapTitle}
                  <ArrowRight className="h-4 w-4 sm:h-5 sm:w-5 opacity-0 group-hover:opacity-100 transition-opacity" />
                </CardTitle>
                <CardDescription className="text-gray-400 text-sm">
                  {t.home.roadmapDesc}
                </CardDescription>
              </CardHeader>
            </Card>
          </Link>
        </div>

        {/* Navigation controls */}
        <NavigationControls />
      </div>
    </div>
  );
}
